import React, { useEffect, useState } from "react";
import {
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormLabel,
  makeStyles,
} from "@material-ui/core";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useParams } from "react-router-dom";
import FormPageLayout from "./FormPageLayout";
import Form from "./Form";
import {
  createTemplate,
  getTemplate,
  updateTemplate,
} from "./formTemplatesApi";

const SNAPSHOT_STORAGE_KEY = "formTemplateDraftSnapshot";

const useStyles = makeStyles((theme) => ({
  container: {
    maxWidth: 1200,
    margin: "0 auto",
  },
  card: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  helperText: {
    color: "#4f6790",
    marginBottom: theme.spacing(2),
  },
}));

const getSnapshotFromStorage = () => {
  try {
    const raw = localStorage.getItem(SNAPSHOT_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return {};
    }
    return parsed;
  } catch (error) {
    console.error("Cannot parse form template snapshot:", error);
    return {};
  }
};

const FormTemplateEditorPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState("");
  const [descriptionNote, setDescriptionNote] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [baseFormType, setBaseFormType] = useState("Eksport");

  useEffect(() => {
    const initialize = async () => {
      if (isEditMode) {
        setLoading(true);
        try {
          const data = await getTemplate(id);
          setName(data.description || "");
          setIsPublic(Number(data.is_public) === 1);
          setBaseFormType(data.form_type || "Eksport");
          setDescriptionNote(data.payload?.meta?.descriptionNote || "");
        } catch (error) {
          console.error("Error loading template details:", error);
          toast.error("Nie udało się pobrać szablonu.");
        } finally {
          setLoading(false);
        }
        return;
      }

      const snapshot = getSnapshotFromStorage();
      if (snapshot.formData?.rodzaj) {
        setBaseFormType(snapshot.formData.rodzaj);
      }
    };

    initialize();
  }, [id, isEditMode]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.warning("Nazwa szablonu jest wymagana.");
      return;
    }

    const latestSnapshot = getSnapshotFromStorage();
    const formTypeFromSnapshot = latestSnapshot?.formData?.rodzaj;

    if (!formTypeFromSnapshot && !baseFormType) {
      toast.warning("Uzupełnij formularz szablonu przed zapisem.");
      return;
    }

    setSaving(true);
    try {
      const body = {
        description: name.trim(),
        form_type: formTypeFromSnapshot || baseFormType,
        is_public: Boolean(isPublic),
        is_favorite: false,
        payload: {
          ...latestSnapshot,
          meta: {
            ...(latestSnapshot?.meta || {}),
            descriptionNote,
            savedAt: new Date().toISOString(),
          },
        },
      };

      if (isEditMode) {
        await updateTemplate(id, body);
        navigate("/form/templates", {
          state: { toastMessage: "Szablon został zaktualizowany." },
        });
      } else {
        await createTemplate(body);
        navigate("/form/templates", {
          state: { toastMessage: "Szablon został utworzony." },
        });
      }
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Nie udało się zapisać szablonu.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormPageLayout activeItem="templates">
      <div className={classes.container}>
        <Paper elevation={3} className={classes.card}>
          <Typography variant="h4" className={classes.title}>
            {isEditMode
              ? "Edytuj szablon zgłoszenia"
              : "Utwórz szablon zgłoszenia"}
          </Typography>

          <Typography variant="h6" style={{ marginBottom: 12 }}>
            Informacje o szablonie
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nazwa szablonu*"
                variant="outlined"
                size="small"
                value={name}
                onChange={(event) => setName(event.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Opis"
                variant="outlined"
                size="small"
                value={descriptionNote}
                onChange={(event) => setDescriptionNote(event.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Widoczność</FormLabel>
                <RadioGroup
                  row
                  value={isPublic ? "public" : "private"}
                  onChange={(event) =>
                    setIsPublic(event.target.value === "public")
                  }
                >
                  <FormControlLabel
                    value="private"
                    control={<Radio color="primary" />}
                    label="Prywatny"
                  />
                  <FormControlLabel
                    value="public"
                    control={<Radio color="primary" />}
                    label="Publiczny"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={1}>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={saving || loading}
              >
                {saving
                  ? "Zapisywanie..."
                  : isEditMode
                    ? "Zapisz zmiany"
                    : "Utwórz szablon"}
              </Button>
            </Grid>
            <Grid item>
              <Button onClick={() => navigate("/form/templates")}>
                Anuluj
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Form
          embedded
          templateBuilderMode
          forcedTemplateId={isEditMode ? id : null}
          onTemplateSave={handleSave}
          templateSaveInProgress={saving || loading}
        />
      </div>
      <ToastContainer position="top-center" />
    </FormPageLayout>
  );
};

export default FormTemplateEditorPage;
